import React, { useState, useMemo, useEffect } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { userService } from '../../services/userService';
import { useAuth } from '../../providers/AuthProvider';

// Fonction pour extraire le numéro de projet (ex: web-tp-projet-05-19 -> 19)
function extractProjectNumber(projectName: string): string {
  // Extraire le dernier numéro du format web-tp-projet-XX-YY
  const match = projectName.match(/-(\d+)$/);
  if (match) {
    return match[1]; // 19
  }
  // Si le format ne correspond pas, retourner le nom tel quel
  return projectName;
}

// Utilitaire pour extraire toutes les user stories présentes dans les résultats affichés
// et les trier selon l'ordre défini dans courseConfig
function getAllUserStories(results: any[], courseConfig: any, cycleId: string): Array<{key: string, code: string, points: number}> {
  // Trouver l'ordre défini dans courseConfig
  const cycle = courseConfig?.cycles?.find?.((c: any) => String(c.id) === String(cycleId));
  if (!cycle || !cycle.user_stories) {
    // Fallback: extraire uniquement des résultats si pas de config
    const usSet = new Set<string>();
    results.forEach(r => {
      if (r.us_e2e_tests) {
        Object.keys(r.us_e2e_tests).forEach(us => usSet.add(us));
      }
    });
    return Array.from(usSet).sort().map(key => ({ key, code: key, points: 0 }));
  }

  // Utiliser TOUTES les user stories définies dans la config (pas seulement celles présentes dans les données)
  return cycle.user_stories.map((us: any) => {
    // Convertir l'id en format "us_X" ou "ts_X"
    // "TP.US1" -> "US1", "PRJ.US2_TS1" -> "US2_TS1", "PREPA.TS2.1" -> "TS2_1" (clé CI ts_2_1)
    const code = us.code.split('.').slice(1).join('_');
    // Gérer les codes simples (US1, TS2) et complexes (US2_TS1, US2_TS2)
    // Convertir en minuscules, ajouter underscore entre lettre et chiffre, mais pas entre _TS et chiffre
    const usKey = code.toLowerCase().replace(/([a-z])(\d)/g, '$1_$2').replace(/_ts_/g, '_ts');
    // "US1" -> "us_1", "TS2" -> "ts_1", "US2_TS1" -> "us_2_ts1"
    return {
      key: usKey,
      code: us.code,
      points: us.story_points || 0
    };
  });
}
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { selectCourseConfig } from '../../store/appDataStore';
import { selectDataRefreshNonce, selectUserInfos } from '../../store/userDataStore';
import { tdGroupService } from '../../services/tdGroupService';
import { CycleResultsPage as CycleResultsPageData } from '../../services/userService';

const PER_PAGE = 25;

import styles from './CycleResultsPage.module.css';
import { Box, CircularProgress } from '@mui/material';

const CycleResultsPage: React.FC = () => {

  const courseConfig = useSelector(selectCourseConfig);
  const userInfos = useSelector(selectUserInfos);
  const refreshNonce = useSelector(selectDataRefreshNonce);
  // Jeton obtenu à chaque requête via getValidToken() (rafraîchi si besoin) : ne pas garder
  // le jeton brut, il expire pendant qu'une page reste ouverte.
  const { authState, getValidToken } = useAuth();
  const isAuthenticated = authState.isAuthenticated;

  const { cycleId } = useParams();


  // Détection du rôle (enseignant ou étudiant)
  const isTeacher = userInfos?.access === 'teacher' || userInfos?.access === 'admin';
  // Suppression d'une trace : admin ou membre du groupe projet (miroir du contrôle fait par l'API)
  const isAdmin = userInfos?.access === 'admin';
  const canDelete = (result: any) =>
    isAdmin || (!!userInfos?.project_group?.id && userInfos.project_group.id === result.project_group_id);

  // States pour les filtres (enseignant uniquement) - DOIVENT être avant le early return
  const [showOnlyLatest, setShowOnlyLatest] = useState(false);
  const [filterTdGroup, setFilterTdGroup] = useState<string>('');
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterMember, setFilterMember] = useState<string>('');

  // State pour les rapports ouverts (plusieurs possibles) - DOIT être ici
  const [openedReports, setOpenedReports] = useState<{resultId: number, indicator: string}[]>([]);

  // State pour afficher les détails de plusieurs user stories - DOIT être ici
  const [openedUserStories, setOpenedUserStories] = useState<{resultId: number, us: string}[]>([]);

  // State pour stocker les rapports chargés dynamiquement
  const [loadedReports, setLoadedReports] = useState<{[resultId: number]: any}>({});

  // State pour tracker les fichiers ZIP disponibles par groupe
  // Clé « groupe|branche » : un cycle peut regrouper plusieurs branches (cycle préparatoire :
  // prepa-student-A et prepa-student-B), chacune avec son propre zip de rapports.
  const [availableZips, setAvailableZips] = useState<{[groupAndBranch: string]: boolean}>({});

  // OPTIMISATION: State pour stocker les project groups avec membres (chargés séparément)
  const [projectGroups, setProjectGroups] = useState<{[groupId: number]: any}>({});

  // On cherche le cycle par id (qui peut être string ou number) - DOIT être avant les useEffect
  const cycle = courseConfig?.cycles?.find?.((c: any) => String(c.id) === String(cycleId));

  // Page de résultats demandée au serveur (filtres + pagination côté API, refonte Charts)
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState<CycleResultsPageData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Recherche par membre : envoyée au serveur après une courte pause de frappe
  const [debouncedMember, setDebouncedMember] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedMember(filterMember.trim()), 350);
    return () => clearTimeout(timer);
  }, [filterMember]);

  // Listes pour les filtres : groupes TD (API) et binômes (groupes projet chargés plus bas)
  const [tdGroups, setTdGroups] = useState<string[]>([]);
  useEffect(() => {
    if (!isTeacher) return;
    (async () => {
      const token = await getValidToken();
      if (!token) return;
      try {
        setTdGroups((await tdGroupService.list(token)).map(g => g.code));
      } catch (error) {
        console.error('Erreur lors du chargement des groupes TD:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  // Retour à la première page quand un filtre change
  useEffect(() => {
    setPage(1);
  }, [cycleId, filterTdGroup, filterProject, debouncedMember, showOnlyLatest]);

  // OPTIMISATION: Charger les project groups avec membres séparément (enseignants uniquement)
  useEffect(() => {
    const loadProjectGroups = async () => {
      if (!isTeacher || !isAuthenticated) return;

      try {
        const accessToken = await getValidToken();
        if (!accessToken) return;
        const groups = await userService.getProjectGroups(accessToken);
        const groupsMap: {[groupId: number]: any} = {};
        groups.forEach((group: any) => {
          groupsMap[group.id] = group;
        });
        setProjectGroups(groupsMap);
      } catch (error) {
        console.error('Erreur lors du chargement des groupes projet:', error);
      }
    };

    loadProjectGroups();
  }, [isTeacher, isAuthenticated, getValidToken]);

  const projectNames = useMemo(() => Object.values(projectGroups)
    .filter((g: any) => g.name && (!filterTdGroup || g.td_group === filterTdGroup))
    .map((g: any) => g.name as string)
    .sort(), [projectGroups, filterTdGroup]);

  // Le binôme choisi n'appartient plus au groupe TD choisi : on retire le filtre binôme
  useEffect(() => {
    if (filterTdGroup && filterProject && !projectNames.includes(filterProject)) {
      setFilterProject('');
    }
  }, [filterTdGroup, filterProject, projectNames]);

  useEffect(() => {
    if (!cycleId || !isAuthenticated) return;
    const projectGroupId = filterProject
      ? Object.values(projectGroups).find((g: any) => g.name === filterProject)?.id
      : undefined;
    if (filterProject && !projectGroupId) return; // groupes projet pas encore chargés
    const controller = new AbortController();
    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const accessToken = await getValidToken();
        if (!accessToken) throw new Error('Session expirée, reconnectez-vous.');
        setPageData(await userService.getCycleResultsPage(cycleId, {
          page, perPage: PER_PAGE,
          tdGroup: isTeacher ? filterTdGroup : undefined,
          projectGroupId: isTeacher ? projectGroupId : undefined,
          member: isTeacher ? debouncedMember : undefined,
          latestOnly: isTeacher && showOnlyLatest,
        }, accessToken, controller.signal));
      } catch (error: any) {
        if (error.name !== 'AbortError') setLoadError(error.message || 'Erreur lors du chargement des résultats');
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, page, filterTdGroup, filterProject, debouncedMember, showOnlyLatest, isTeacher, isAuthenticated,
      projectGroups, refreshNonce, reloadKey]);

  // Résultats de la page affichée, déjà filtrés et triés (du plus récent au plus ancien) par l'API
  const results: any[] = pageData?.results || [];

  // Vérifier l'existence des fichiers ZIP pour les groupes visibles
  useEffect(() => {
    const checkZipAvailability = async () => {
      if (!cycle || !isAuthenticated || results.length === 0) return;

      const pairs = new Map<string, { groupId: number; branch: string }>();
      results
        .filter((r: any) => r.is_latest_of_branch)
        .forEach((r: any) => {
          if (r.project_group_id) {
            const branch = r.branch || cycle.short_name;
            pairs.set(`${r.project_group_id}|${branch}`, { groupId: r.project_group_id, branch });
          }
        });

      const accessToken = await getValidToken();
      if (!accessToken) return;
      const checks = Array.from(pairs.entries()).map(async ([key, { groupId, branch }]) => {
        try {
          const exists = await userService.checkReportZipExists(groupId, branch, accessToken);
          return { key, exists };
        } catch (error) {
          return { key, exists: false };
        }
      });

      const zipChecks = await Promise.all(checks);
      const zipMap: {[groupAndBranch: string]: boolean} = {};
      zipChecks.forEach(({ key, exists }) => {
        zipMap[key] = exists;
      });
      setAvailableZips(zipMap);
    };

    checkZipAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData, cycle, isAuthenticated]);

  if (!cycle) {
    return (
      <div>
        <Box className={styles['cycle-results-page']}>
          <h2>Le cycle {cycleId} n'existe pas.</h2>
        </Box>
      </div>
    );
  }

  // Cycle préparatoire : plusieurs branches (prepa-student-A / B) -> colonne « Branche » (A, B...)
  const hasSeveralBranches = (cycle?.branches?.length ?? 0) > 1;
  const branchLabel = (branch?: string | null) =>
    branch ? branch.replace('prepa-student-', '').replace('prepa-', '') : '—';

  // Indicateurs à afficher
  const indicators = [
    // Lint = Ruff avec la configuration du projet (tri des imports compris) : plus de colonne isort
    { key: 'lint_errors', label: 'Lint (Ruff)', report: 'lint_error_report' },
    { key: 'mypy_errors', label: 'MyPy', report: 'mypy_error_report' },
    { key: 'html_errors', label: 'HTML', report: 'html_error_report' },
    { key: 'css_errors', label: 'CSS', report: 'css_error_report' },
    // Ajout du score Git comme indicateur spécial
    { key: 'git_diff_score_first_commit', label: 'Score Git', report: 'git_diff_report_first_commit', isGit: true },
  ];

  // Notes de qualité (lettres A-E calculées par Ruff) : un clic affiche les problèmes qui expliquent la note.
  // La sécurité est affichée mais ne compte pas dans le score global.
  const qualityIndicators = [
    { key: 'reliability_score', label: 'Fiabilité', report: 'reliability_report' },
    { key: 'maintainability_score', label: 'Maintenabilité', report: 'maintainability_report' },
    { key: 'security_score', label: 'Sécurité', report: 'security_report' },
  ];
  const reportIndicators = [...indicators, ...qualityIndicators];

  const handleIndicatorClick = async (resultId: number, indicator: string) => {
    const idx = openedReports.findIndex(r => r.resultId === resultId && r.indicator === indicator);
    if (idx !== -1) {
      // Si déjà ouvert, on le retire (toggle)
      setOpenedReports(openedReports.filter((_, i) => i !== idx));
    } else {
      // Vérifier si on a déjà les données du rapport pour ce résultat
      const result = results.find((r: any) => r.id === resultId);
      const indicatorObj = reportIndicators.find(ind => ind.key === indicator);

      // Si le rapport n'existe pas, le charger via l'API
      if (result && indicatorObj && !result[indicatorObj.report] && !loadedReports[resultId]) {
        const accessToken = await getValidToken();
        if (!accessToken) {
          console.error('Token d\'accès manquant, impossible de charger les détails');
          setOpenedReports([...openedReports, { resultId, indicator }]);
          return;
        }

        try {
          console.log(`Chargement des détails pour le résultat ${resultId}...`);
          const details = await userService.getCycleResultDetails(resultId, accessToken);

          // Stocker les détails chargés
          setLoadedReports(prev => ({
            ...prev,
            [resultId]: details
          }));

          // Mettre à jour le résultat dans la liste avec les rapports chargés
          // Note: ceci ne modifie que l'affichage local, pas le store Redux
          Object.assign(result, details);
        } catch (error) {
          console.error('Erreur lors du chargement des détails:', error);
        }
      }

      setOpenedReports([...openedReports, { resultId, indicator }]);
    }
  };

  // Colonnes dynamiques pour les user stories
  const userStories = getAllUserStories(results, courseConfig, cycleId || '');

  const handleUserStoryClick = async (resultId: number, us: string) => {
    const idx = openedUserStories.findIndex(r => r.resultId === resultId && r.us === us);
    if (idx !== -1) {
      // Si déjà ouvert, on le retire (toggle)
      setOpenedUserStories(openedUserStories.filter((_, i) => i !== idx));
      return;
    }

    // Vérifier si on a déjà les détails complets pour ce résultat
    const result = results.find((r: any) => r.id === resultId);
    const loadedData = loadedReports[resultId];
    const usData = loadedData?.us_e2e_tests?.[us] || result?.us_e2e_tests?.[us];

    // Charger les détails si :
    // 1. Les données ne sont pas déjà chargées (pas dans loadedReports)
    // 2. ET (usData n'existe pas OU usData.tests n'existe pas OU usData.tests est un tableau vide)
    const needsLoading = !loadedData && (!usData || !usData.tests || usData.tests.length === 0);

    if (result && needsLoading) {
      const accessToken = await getValidToken();
      if (!accessToken) {
        console.error('Token d\'accès manquant, impossible de charger les détails');
        setOpenedUserStories([...openedUserStories, { resultId, us }]);
        return;
      }

      try {
        const details = await userService.getCycleResultDetails(resultId, accessToken);

        // Stocker les détails chargés
        setLoadedReports(prev => ({
          ...prev,
          [resultId]: details
        }));

        // Mettre à jour le résultat dans la liste avec les détails E2E chargés
        Object.assign(result, details);

        // Ouvrir l'élément APRÈS le chargement des données
        setOpenedUserStories([...openedUserStories, { resultId, us }]);
      } catch (error) {
        console.error('Erreur lors du chargement des détails E2E:', error);
        // Ouvrir quand même pour montrer le message d'erreur
        setOpenedUserStories([...openedUserStories, { resultId, us }]);
      }
    } else {
      // Les données sont déjà là, ouvrir directement
      setOpenedUserStories([...openedUserStories, { resultId, us }]);
    }
  };

  // Fonction pour télécharger le rapport ZIP
  const handleDownloadReport = async (groupId: number, branch: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!cycle || !isAuthenticated) return;

    try {
      const accessToken = await getValidToken();
      if (!accessToken) return;
      await userService.downloadReportZip(groupId, branch, accessToken);
    } catch (error) {
      console.error('Erreur lors du téléchargement du rapport:', error);
      alert('Erreur lors du téléchargement du rapport');
    }
  };

  // Supprimer une trace de cycle, puis la retirer du store (pas de rechargement complet)
  const handleDeleteResult = async (result: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const label = `${new Date(result.date).toLocaleString()}${result.commit_name ? ` (« ${result.commit_name} »)` : ''}`;
    if (!window.confirm(`Supprimer définitivement la trace du ${label} ?`)) return;

    try {
      const accessToken = await getValidToken();
      if (!accessToken) return;
      await userService.deleteCycleResult(result.id, accessToken);
      setReloadKey(k => k + 1);
      setOpenedReports(prev => prev.filter(r => r.resultId !== result.id));
      setOpenedUserStories(prev => prev.filter(r => r.resultId !== result.id));
    } catch (error) {
      console.error('Erreur lors de la suppression du résultat:', error);
      alert(`Erreur lors de la suppression : ${(error as Error).message}`);
    }
  };

  // Loader plein écran seulement au premier chargement (ensuite : filtres et tableau restent affichés)
  if (isLoading && !pageData) {
    return (
      <div>
        <Box className={styles['cycle-results-page']}>
          <h2>Résultats pour le cycle : {cycle.short_name.toUpperCase()}</h2>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </div>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <Box className={styles['cycle-results-page']}>
        <h2>Résultats pour le cycle : {cycle.short_name.toUpperCase()}</h2>
        {/* Filtres pour enseignants */}
        {isTeacher && (
          <div className={styles['filters-container']}>
            <label>
              <input
                type="checkbox"
                checked={showOnlyLatest}
                onChange={(e) => setShowOnlyLatest(e.target.checked)}
              />
              Afficher uniquement le dernier résultat par projet
            </label>
            <label>
              Groupe TD :
              <select value={filterTdGroup} onChange={(e) => setFilterTdGroup(e.target.value)}>
                <option value="">Tous</option>
                {tdGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </label>
            <label>
              Projet :
              <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
                <option value="">Tous</option>
                {projectNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </label>
            <label>
              Membre :
              <input
                type="text"
                value={filterMember}
                onChange={(e) => setFilterMember(e.target.value)}
                placeholder="Rechercher par nom..."
              />
            </label>
          </div>
        )}

        <table className={styles['results-table']}>
          <thead>
            <tr>
              {isTeacher && <th className={styles['centered']}>Groupe TD</th>}
              {isTeacher && <th className={styles['centered']}>Groupe Projet</th>}
              {isTeacher && <th className={styles['centered']}>Membres</th>}
              <th>Commit</th>
              {hasSeveralBranches && <th className={styles['centered']}>Branche</th>}
              {/* Indicateurs classiques */}
              {indicators.filter(ind => !ind.isGit).map(ind => (
                <th key={ind.key} className={styles['centered']}>{ind.label}</th>
              ))}
              {/* Colonnes user stories dynamiques */}
              {userStories.map(us => (
                <th key={us.key} className={styles['centered']}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span>{us.code.split('.').slice(1).join('.')}</span>
                    <span style={{ fontSize: '0.75em', fontWeight: 'normal' }}>{us.points}{us.points > 1 ? 'pts' : 'pt'}</span>
                  </div>
                </th>
              ))}
              {/* <th>Score Git</th> */}
              {qualityIndicators.map(ind => (
                <th key={ind.key} className={styles['centered']}>{ind.label}</th>
              ))}
              <th className={styles['centered']}>Score global</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result: any, idx: number) => {
              // Pour les étudiants : premier résultat = le plus récent
              // Pour les enseignants : vérifier si c'est le dernier résultat de ce groupe
              // Dernier résultat du groupe / de sa branche sur TOUT le cycle (calculé par l'API, la page
              // n'affichant qu'une partie des résultats)
              const isLatest = !!result.is_latest_of_group;
              // Zip de rapports : proposé sur le dernier résultat de chaque branche du groupe
              const zipBranch = result.branch || cycle.short_name;
              const isLatestOfBranch = !!result.is_latest_of_branch;
              const rowClass = isLatest
                ? styles['results-row-latest']
                : idx % 2 === 0
                  ? styles['results-row']
                  : styles['results-row-blue'];
              const cellClass = isLatest ? styles['results-cell-latest'] : '';
              return (
                <React.Fragment key={result.id}>
                  <tr className={`${styles['results-row']} ${rowClass}`}>
                    {isTeacher && (
                      <td className={`${cellClass} ${styles['centered']}`}>
                        {result.project_group?.td_group || 'N/A'}
                      </td>
                    )}
                    {isTeacher && (
                      <td className={`${cellClass} ${styles['centered']}`}>
                        {result.project_group?.name ? extractProjectNumber(result.project_group.name) : 'N/A'}
                      </td>
                    )}
                    {isTeacher && (
                      <td className={`${cellClass} ${styles['centered']}`}>
                        {(() => {
                          // OPTIMISATION: Récupérer les membres depuis projectGroups
                          const group = projectGroups[result.project_group_id];
                          const members = group?.members || [];
                          return (
                            <span
                              style={{ fontSize: '0.85em', cursor: 'help' }}
                              title={members.map((m: any) =>
                                `${m.firstname} ${m.lastname}`
                              ).join(', ') || 'N/A'}
                            >
                              {members.map((m: any) =>
                                `${m.firstname.charAt(0)}${m.lastname.charAt(0)}`
                              ).join(', ') || 'N/A'}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td className={cellClass}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={result.commit_name ? { fontSize: '0.85em' } : undefined}>
                            {new Date(result.date).toLocaleDateString()}
                          </span>
                          {result.commit_name && (
                            <span
                              style={{ fontSize: '0.85em', color: '#666', fontStyle: 'italic' }}
                              title={result.commit_name}
                            >
                              {result.commit_name.length > (isTeacher ? 10 : 25)
                                ? `${result.commit_name.substring(0, isTeacher ? 10 : 25)}...`
                                : result.commit_name}
                            </span>
                          )}
                        </div>
                        {/* Icône de téléchargement du rapport ZIP - uniquement pour le dernier résultat */}
                        {isLatestOfBranch && availableZips[`${result.project_group_id}|${zipBranch}`] && (
                          <DownloadIcon
                            style={{
                              fontSize: '1.2em',
                              color: '#1976d2',
                              cursor: 'pointer',
                              flexShrink: 0
                            }}
                            onClick={(e) => handleDownloadReport(result.project_group_id, zipBranch, e)}
                            titleAccess="Télécharger le rapport des tests"
                          />
                        )}
                        {canDelete(result) && (
                          <DeleteIcon
                            style={{
                              fontSize: '1.2em',
                              color: '#d32f2f',
                              cursor: 'pointer',
                              flexShrink: 0
                            }}
                            onClick={(e) => handleDeleteResult(result, e)}
                            titleAccess="Supprimer cette trace"
                          />
                        )}
                      </div>
                    </td>
                    {hasSeveralBranches && (
                      <td className={`${cellClass} ${styles['centered']}`} title={result.branch || ''}>
                        {branchLabel(result.branch)}
                      </td>
                    )}
                    {/* Indicateurs classiques */}
                    {indicators.filter(ind => !ind.isGit).map(ind => {
                      const isOpen = openedReports.some(r => r.resultId === result.id && r.indicator === ind.key);
                      const hasValue = result[ind.key] !== undefined && result[ind.key] !== null;
                      return (
                        <td key={ind.key} className={`${cellClass} ${styles['centered']}`}>
                          <button
                            className={[
                              styles['results-indicator-btn'],
                              isOpen ? styles['opened'] : '',
                              result[ind.key] > 0 ? styles['bold'] : ''
                            ].join(' ')}
                            disabled={!hasValue}
                            onClick={() => handleIndicatorClick(result.id, ind.key)}
                          >
                            {result[ind.key] ?? 0}
                          </button>
                        </td>
                      );
                    })}
                    {/* Colonnes user stories dynamiques */}
                    {userStories.map(us => {
                      const usData = result.us_e2e_tests?.[us.key];
                      const isOpen = openedUserStories.some(r => r.resultId === result.id && r.us === us.key);
                      // Tous les scores sont en gras pour la visibilité
                      const isBold = true;
                      return (
                        <td key={us.key} className={`${cellClass} ${styles['centered']}`}>
                          {usData ? (
                            <button
                              className={[
                                styles['results-indicator-btn'],
                                isOpen ? styles['opened'] : '',
                                isBold ? styles['bold'] : ''
                              ].join(' ')}
                              onClick={() => handleUserStoryClick(result.id, us.key)}
                            >
                              {usData.score}
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                      );
                    })}
                    {/* Score Git cliquable (masqué) */}
                    {/* <td className={cellClass}>
                      <button
                        className={[
                          styles['results-indicator-btn'],
                          openedReports.some(r => r.resultId === result.id && r.indicator === 'git_diff_score_first_commit') ? styles['opened'] : '',
                          result.git_diff_score_first_commit > 0 ? styles['bold'] : ''
                        ].join(' ')}
                        disabled={typeof result.git_diff_score_first_commit !== 'number' || !result.git_diff_report_first_commit}
                        onClick={() => handleIndicatorClick(result.id, 'git_diff_score_first_commit')}
                      >
                        {result.git_diff_score_first_commit ?? ''}
                      </button>
                    </td> */}
                    {/* Notes de qualité cliquables (détail des problèmes Ruff) */}
                    {qualityIndicators.map(ind => {
                      const isOpen = openedReports.some(r => r.resultId === result.id && r.indicator === ind.key);
                      const letter = result[ind.key];
                      // Pipeline plantée ('-') ou pas de note : rien à ouvrir (un ancien résultat radon/bandit
                      // s'ouvre sur « Rapport non disponible »)
                      const hasLetter = typeof letter === 'string' && /^[A-E]$/.test(letter);
                      return (
                        <td key={ind.key} className={`${cellClass} ${styles['centered']}`}>
                          <button
                            className={[
                              styles['results-indicator-btn'],
                              isOpen ? styles['opened'] : '',
                              hasLetter && letter !== 'A' ? styles['bold'] : ''
                            ].join(' ')}
                            disabled={!hasLetter}
                            onClick={() => handleIndicatorClick(result.id, ind.key)}
                          >
                            {letter ?? ''}
                          </button>
                        </td>
                      );
                    })}
                    <td className={`${cellClass} ${styles['centered']}`}>{result.global_score ?? ''}</td>
                  </tr>
                  {/* Affichage des détails de la user story sélectionnée */}
                  {userStories.map(us => {
                    // Chercher d'abord dans les données chargées, puis dans le résultat initial
                    const loadedData = loadedReports[result.id];
                    const usData = loadedData?.us_e2e_tests?.[us.key] || result.us_e2e_tests?.[us.key];
                    const isOpen = openedUserStories.some(r => r.resultId === result.id && r.us === us.key);
                    // Chercher le descriptif de la user story dans courseConfig (par code)
                    let usDescription = '';
                    if (courseConfig && courseConfig.cycles) {
                      const cycleObj = courseConfig.cycles.find((c: any) => String(c.id) === String(result.cycle_id));
                      if (cycleObj && cycleObj.user_stories) {
                        // Chercher par code (ex: "PRJ.US2_TS1") plutôt que par id numérique
                        const usObj = cycleObj.user_stories.find((u: any) => u.code === us.code);
                        if (usObj) usDescription = usObj.description;
                      }
                    }
                    if (usData && isOpen) {
                      return (
                        <tr key={us.key + '-details'} className={styles['results-report-row']}>
                          <td colSpan={(isTeacher ? 3 : 0) + indicators.length + userStories.length + 5} style={{ padding: '1em' }}>
                            <strong>Tests pour {us.code}{usDescription ? ` (${usDescription})` : ''} :</strong>
                            {usData.tests && usData.tests.length > 0 ? (
                              <ul style={{ margin: '0.5em 0 0 1.5em', padding: 0 }}>
                                {usData.tests.map((test: any, i: number) => (
                                  <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                    {test.status === 'passed' ? (
                                      <CheckCircleIcon style={{ color: 'green', marginRight: 6 }} fontSize="small" />
                                    ) : (
                                      <CancelIcon style={{ color: 'red', marginRight: 6 }} fontSize="small" />
                                    )}
                                    <span style={{ fontWeight: 500 }}>{test.description}</span>
                                    {test.error && (
                                      <span style={{ color: 'red', marginLeft: 8 }}>— {test.error}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p style={{ fontStyle: 'italic', color: '#666', margin: '0.5em 0' }}>
                                Détails des tests non disponibles ou en cours de chargement...
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    }
                    return null;
                  })}
                  {/* Affichage de TOUS les rapports ouverts pour cette ligne, y compris git (inchangé) */}
                  {openedReports.filter(r => r.resultId === result.id).map((r, i) => {
                    const ind = reportIndicators.find(ind => ind.key === r.indicator);
                    // Chercher le rapport d'abord dans les données chargées, puis dans le résultat initial
                    const loadedData = loadedReports[result.id];
                    const reportContent = loadedData?.[ind?.report || ''] || result[ind?.report || ''];
                    return (
                      <tr key={r.indicator + '-' + i} className={styles['results-report-row']}>
                        <td colSpan={(isTeacher ? 3 : 0) + indicators.length + userStories.length + 5} style={{ padding: '1em' }}>
                          <strong>Rapport {ind?.label} :</strong>
                          {reportContent ? (
                            <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                              {reportContent}
                            </pre>
                          ) : (
                            <p style={{ fontStyle: 'italic', color: '#666', margin: '0.5em 0' }}>
                              Rapport non disponible
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {loadError && <p style={{ color: '#c62828' }}>{loadError}</p>}
        {pageData && pageData.pagination.total === 0 && <p>Aucun résultat pour ces filtres.</p>}
        {pageData && pageData.pagination.pages > 1 && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
            <button disabled={!pageData.pagination.has_prev || isLoading} onClick={() => setPage(p => p - 1)}>← Plus récents</button>
            <span>
              Page {pageData.pagination.page} / {pageData.pagination.pages} · {pageData.pagination.total} résultat(s)
            </span>
            <button disabled={!pageData.pagination.has_next || isLoading} onClick={() => setPage(p => p + 1)}>Plus anciens →</button>
            {isLoading && <CircularProgress size={18} />}
          </div>
        )}
      </Box>
    </div>
  );
};

export default CycleResultsPage;
